import React from 'react';
import type { PluginDefinition } from './types';
import { Brain, MessageCircleQuestion, LayoutDashboard, ScrollText, Cpu, BarChart3, ShieldCheck, FileText, Workflow, Activity, GitBranch } from 'lucide-react';
import { connectionStatusPlugin } from './connection-status';
import { connectorHubPlugin } from './connector-hub';
import { chunkVisualizerPlugin } from './chunk-visualizer';
import { helpAssistantPlugin } from './help-assistant';
import { stewardPlugin } from './steward';
import { uploaderPlugin } from './uploader';
import { SuiteDashboard } from '@/app/features/suite-dashboard/SuiteDashboard';
import { RolloutKpiPage } from '@/app/features/rollout-kpi/RolloutKpiPage';
import { GovernancePage } from '@/app/features/governance/GovernancePage';
import { ClarifyPage } from '@/app/features/clarify/ClarifyPage';
import { DocumentBrowserPage } from '@/app/features/document-browser/DocumentBrowserPage';
import { SmartEditorPanel } from '@/app/features/editor/components/SmartEditorPanel';
import { KnowledgeGraphPanel } from '@/app/features/editor/components/KnowledgeGraphPanel';
import { EntityInspectorPanel } from '@/app/features/editor/components/EntityInspectorPanel';
import { OntologyBrowserPanel } from '@/app/features/editor/components/OntologyBrowserPanel';
import { InterviewerPage } from '@/app/features/interviewer/InterviewerPage';
import SophoOntologyPage from '../app/features/sopho-ontology/SophoOntologyPage';
import { APP_VERSION } from '@/app/version';

// ─── Lazy-loaded feature pages ─────────────────────────────────────────────────

const TalkToDataPage = React.lazy(
  () => import('@/app/features/talk-to-data/TalkToDataPage'),
);

const RulesetAnalyzerPage = React.lazy(
  () => import('@/app/features/ruleset-analyzer/RulesetAnalyzerPage'),
);

const FlowsPage = React.lazy(
  () => import('@/app/features/sopho-flow/FlowsPage'),
);

const FlowEditorPage = React.lazy(
  () => import('@/app/features/sopho-flow/FlowEditorPage'),
);

const SophoFusePage = React.lazy(
  () => import('@/app/features/sopho-fuse/SophoFusePage'),
);

// ─── Plugin definitions ────────────────────────────────────────────────────────

const dashboardPlugin: PluginDefinition = {
  id: 'core-dashboard',
  name: 'Sopho Dashboard',
  version: APP_VERSION,
  description: 'Workspace-Übersicht und Einstieg.',
  internal: true,
  locked: true,
  group: 'core',
  initializationOrder: 0,
  initialize: () => undefined,
  navigation: [
    {
      id: 'dashboard',
      label: 'Sopho Dashboard',
      path: '/app/dashboard',
      icon: LayoutDashboard,
      order: 0,
    },
  ],
  routes: [
    {
      path: 'dashboard',
      element: <SuiteDashboard />,
    },
  ],
};

const aiInterviewerPlugin: PluginDefinition = {
  id: 'ai-interviewer',
  name: 'Sopho Interviewer',
  version: APP_VERSION,
  description: 'Geführte Datenerfassung mit KI-Unterstützung.',
  internal: true,
  group: 'analysis',
  initializationOrder: 20,
  initialize: () => undefined,
  navigation: [
    {
      id: 'ai-interviewer',
      label: 'Sopho Interviewer',
      path: '/app/interviewer',
      icon: Brain,
      order: 20,
    },
  ],
  routes: [
    {
      path: 'interviewer',
      element: <InterviewerPage />,
    },
  ],
};

const sophoOntologyPlugin: PluginDefinition = {
  id: 'sopho_ontology',
  name: 'Sopho Ontology',
  version: APP_VERSION,
  description: 'Graphischer Ontologie-Explorer für Concepts, Relationen und Klassifikationen.',
  internal: true,
  group: 'admin',
  // DevPlan 0139/T3: Ontologie eines konkreten Workspace → nur sichtbar für WS-Admin (Org-Admin via Bypass)
  accessControl: {
    minWorkspaceRole: 'Admin',
    features: {
      'edit-ontology': { minWorkspaceRole: 'Admin' },
      'delete-ontology': { minWorkspaceRole: 'Admin' },
    },
  },
  initializationOrder: 25,
  initialize: () => undefined,
  navigation: [
    {
      id: 'sopho_ontology',
      label: 'Sopho Ontology',
      path: '/app/ontology',
      icon: GitBranch,
      order: 25,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      id: 'sopho_ontology',
      path: 'ontology',
      element: (
        <React.Suspense fallback={<div className="flex h-full items-center justify-center text-muted-foreground text-sm">Lädt…</div>}>
          <SophoOntologyPage />
        </React.Suspense>
      ),
    },
  ],
};

const talkToDataPlugin: PluginDefinition = {
  id: 'talk-to-your-data',
  name: 'Sopho Chat',
  version: APP_VERSION,
  description: 'Frage deinen Workspace in natürlicher Sprache.',
  internal: true,
  group: 'analysis',
  initializationOrder: 30,
  initialize: () => undefined,
  navigation: [
    {
      id: 'talk-to-your-data',
      label: 'Sopho Chat',
      path: '/app/talk-to-data',
      icon: MessageCircleQuestion,
      order: 30,
    },
  ],
  routes: [
    {
      path: 'talk-to-data',
      element: (
        <React.Suspense fallback={<div className="flex h-full items-center justify-center text-muted-foreground text-sm">Lädt…</div>}>
          <TalkToDataPage />
        </React.Suspense>
      ),
    },
  ],
};

const smartEditorPanelPlugin: PluginDefinition = {
  id: 'smart-editor-panel',
  name: 'Smart Editor Panel',
  version: APP_VERSION,
  description: 'Linkes Editor-Panel für Wissenserfassung.',
  internal: true,
  group: 'core',
  initializationOrder: 11,
  requires: ['knowledge-capture'],
  initialize: () => undefined,
  slots: {
    leftPanel: () => <SmartEditorPanel />,
  },
};

const knowledgeGraphPanelPlugin: PluginDefinition = {
  id: 'knowledge-graph-panel',
  name: 'Knowledge Graph Panel',
  version: APP_VERSION,
  description: 'Mittleres Panel für die Graph-Visualisierung.',
  internal: true,
  group: 'core',
  initializationOrder: 12,
  requires: ['knowledge-capture'],
  initialize: () => undefined,
  slots: {
    middlePanel: () => <KnowledgeGraphPanel />,
  },
};

const entityInspectorPanelPlugin: PluginDefinition = {
  id: 'entity-inspector-panel',
  name: 'Entity Inspector Panel',
  version: APP_VERSION,
  description: 'Rechtes Panel für Entity-Details, Governance und Ontology.',
  internal: true,
  group: 'core',
  initializationOrder: 13,
  requires: ['knowledge-capture'],
  initialize: () => undefined,
  slots: {
    rightPanel: () => (
      <div className="flex h-full flex-col">
        <div className="flex-1">
          <EntityInspectorPanel />
        </div>
        <div className="h-[40%] border-t border-slate-800">
          <OntologyBrowserPanel />
        </div>
      </div>
    ),
  },
};

const rulesetAnalyzerPlugin: PluginDefinition = {
  id: 'ruleset-analyzer',
  name: 'Sopho Ruleset',
  version: APP_VERSION,
  description: 'Regelwerke hochladen, durchsuchen, per RAG befragen und Pipeline überwachen.',
  internal: true,
  group: 'analysis',
  initializationOrder: 35,
  initialize: () => undefined,
  navigation: [
    {
      id: 'ruleset-analyzer',
      label: 'Sopho Ruleset',
      path: '/app/ruleset-analyzer',
      icon: ScrollText,
      order: 35,
    },
  ],
  routes: [
    {
      path: 'ruleset-analyzer',
      element: (
        <React.Suspense fallback={<div className="flex h-full items-center justify-center text-muted-foreground text-sm">Lädt…</div>}>
          <RulesetAnalyzerPage />
        </React.Suspense>
      ),
    },
  ],
};

const MachineRegistryPage = React.lazy(
  () => import('@/features/graph/MachineRegistryPage').then(m => ({ default: m.MachineRegistryPage })),
);

const machineRegistryPlugin: PluginDefinition = {
  id: 'machine-registry',
  name: 'Sopho Machine Models',
  version: APP_VERSION,
  description: 'Registrierung und Verwaltung von Maschinenmodellen für maschinenspezifisches RAG.',
  internal: true,
  locked: true,
  group: 'admin',
  // DevPlan 0139/T3: Maschinenmodelle eines konkreten Workspace → WS-Admin statt Org-Admin
  // (zusätzlich nur in machine-aware Workspaces eingeblendet).
  accessControl: { minWorkspaceRole: 'Admin' },
  initializationOrder: 25,
  initialize: () => undefined,
  navigation: [
    {
      id: 'machine-registry',
      label: 'Sopho Machine Models',
      path: '/app/machines',
      icon: Cpu,
      order: 25,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      id: 'machine-registry',
      path: 'machines',
      element: (
        <React.Suspense fallback={<div className="flex h-full items-center justify-center text-muted-foreground text-sm">Lädt…</div>}>
          <MachineRegistryPage />
        </React.Suspense>
      ),
    },
  ],
};

// ─── Backend-Plugin-Stellvertreter (nur für Settings-UI im Plugin-Manager) ────

const ingestPlugin: PluginDefinition = {
  id: 'ingest',
  name: 'Document Ingest',
  version: APP_VERSION,
  description: 'Verarbeitet Dokumente zu Chunks, Entitäten und Embeddings (Python-Backend).',
  internal: true,
  locked: true,
  group: 'core',
  initializationOrder: 10,
  initialize: () => undefined,
};

const retrievalPlugin: PluginDefinition = {
  id: 'retrieval',
  name: 'Retrieval',
  version: APP_VERSION,
  description: 'Graph- und Vektor-Suche mit optionaler Maschinen-Disambiguierung (Python-Backend).',
  internal: true,
  locked: true,
  group: 'core',
  initializationOrder: 30,
  initialize: () => undefined,
};

const rolloutKpiPlugin: PluginDefinition = {
  id: 'rollout-kpi',
  name: 'Sopho Quality',
  version: APP_VERSION,
  description: 'Qualitätsmetriken für Retrieval, Fehlerquote und Reindex-Fortschritt.',
  internal: true,
  group: 'admin',
  accessControl: { minOrgRole: 'Admin' },
  initializationOrder: 50,
  initialize: () => undefined,
  navigation: [
    {
      id: 'rollout-kpi',
      label: 'Sopho Quality',
      path: '/app/rollout-kpi',
      icon: BarChart3,
      order: 50,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      id: 'rollout-kpi',
      path: 'rollout-kpi',
      element: <RolloutKpiPage />,
    },
  ],
};

const governancePlugin: PluginDefinition = {
  id: 'governance',
  name: 'Sopho Governance',
  version: APP_VERSION,
  description: 'Sicherheit, Compliance und Genehmigungs-Workflows der Organisation.',
  internal: true,
  group: 'admin',
  accessControl: { minOrgRole: 'Admin' },
  initializationOrder: 51,
  initialize: () => undefined,
  navigation: [
    {
      id: 'governance',
      label: 'Sopho Governance',
      path: '/app/governance',
      icon: ShieldCheck,
      order: 51,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      id: 'governance',
      path: 'governance',
      element: <GovernancePage />,
    },
  ],
};

const documentBrowserPlugin: PluginDefinition = {
  id: 'document-browser',
  name: 'Sopho Data Viewer',
  version: APP_VERSION,
  description: 'Alle Dokumente im Workspace durchsuchen und einsehen.',
  internal: true,
  // DevPlan 0139-Follow-up: Gruppe 'data' (nicht 'admin') — reines Lesen/Durchsuchen
  // vorhandener Dokumente ist ein Reader-Tool. Die 'admin'-Gruppe erzeugte ein
  // irreführendes "Admin"-Badge + Administration-Platzierung, obwohl der Gate
  // minWorkspaceRole='Reader' ist (jedes WS-Mitglied darf Dokumente lesen).
  group: 'data',
  accessControl: { minWorkspaceRole: 'Reader' },
  initializationOrder: 22,
  initialize: () => undefined,
  navigation: [
    {
      id: 'document-browser',
      label: 'Sopho Data Viewer',
      path: '/app/documents',
      icon: FileText,
      order: 22,
    },
  ],
  routes: [
    {
      id: 'document-browser',
      path: 'documents',
      element: <DocumentBrowserPage />,
    },
  ],
};

const clarifyPlugin: PluginDefinition = {
  id: 'clarify',
  name: 'Sopho Clarify',
  version: APP_VERSION,
  description: 'Fachanfragen orchestrieren, Experten interviewen und Antworten reviewen.',
  internal: true,
  group: 'analysis',
  accessControl: { minWorkspaceRole: 'Reader' },
  initializationOrder: 21,
  initialize: () => undefined,
  navigation: [
    {
      id: 'clarify',
      label: 'Sopho Clarify',
      path: '/app/clarify',
      icon: FileText,
      order: 21,
    },
  ],
  routes: [
    {
      id: 'clarify',
      path: 'clarify',
      element: <ClarifyPage />,
    },
  ],
};

const sophoFlowPlugin: PluginDefinition = {
  id: 'sopho-flow',
  name: 'Sopho Flows',
  version: APP_VERSION,
  description: 'Visueller KI-Workflow-Editor mit Nodes, Edges und Langflow-Sync.',
  internal: true,
  group: 'admin',
  accessControl: { minOrgRole: 'Admin' },
  initializationOrder: 52,
  initialize: () => undefined,
  navigation: [
    {
      id: 'sopho-flow',
      label: 'Sopho Flows',
      path: '/app/flows',
      icon: Workflow,
      order: 52,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      id: 'sopho-flow',
      path: 'flows',
      element: <FlowsPage />,
    },
    {
      // Gleiche id → derselbe RoleGate (getPluginById); Key entkoppelt in renderPluginRoutes.
      id: 'sopho-flow',
      path: 'flows/:flowId/edit',
      element: <FlowEditorPage />,
    },
  ],
};

const sophoFusePlugin: PluginDefinition = {
  id: 'sopho-fuse',
  name: 'Sopho Fuse',
  version: APP_VERSION,
  description: 'LLM Observability — Langfuse Traces, Kosten & Performance.',
  internal: true,
  group: 'admin',
  accessControl: { minOrgRole: 'Admin' },
  initializationOrder: 53,
  initialize: () => undefined,
  navigation: [
    {
      id: 'sopho-fuse',
      label: 'Sopho Fuse',
      path: '/app/sopho-fuse',
      icon: Activity,
      order: 53,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      id: 'sopho-fuse',
      path: 'sopho-fuse',
      element: <SophoFusePage />,
    },
  ],
};

export const builtinPlugins: PluginDefinition[] = [
  connectionStatusPlugin,
  dashboardPlugin,
  smartEditorPanelPlugin,
  knowledgeGraphPanelPlugin,
  entityInspectorPanelPlugin,
  aiInterviewerPlugin,
  clarifyPlugin,
  sophoOntologyPlugin,
  talkToDataPlugin,
  rulesetAnalyzerPlugin,
  uploaderPlugin,
  connectorHubPlugin,
  stewardPlugin,
  machineRegistryPlugin,
  chunkVisualizerPlugin,
  documentBrowserPlugin,
  ingestPlugin,
  retrievalPlugin,
  rolloutKpiPlugin,
  governancePlugin,
  sophoFlowPlugin,
  sophoFusePlugin,
  helpAssistantPlugin,
];
